import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { motion } from 'framer-motion';
import { CARD_CLASS, OMS_COLORS, baseChartOptions, sectionVariants } from '../../dashboard/omsDashTheme';
import { ADMIN, ADMIN_EYEBROW_CLASS } from '../adminTheme';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import type { FlowPoint, FlowTotals } from '../utils/adminMetrics';
import { RateTrioLegend } from './adminBits';

interface CyclePoint {
  bucket: string;
  label: string;
  medianHours: number | null;
  n: number;
}

interface Props {
  points: FlowPoint[];
  prevPoints: FlowPoint[];
  totals: FlowTotals;
  cycle: CyclePoint[];
  compare: boolean;
  phrase: string;
  loading: boolean;
}

const axisLabels = {
  style: { colors: OMS_COLORS.caption, fontSize: '10px', fontWeight: 600 },
  hideOverlappingLabels: true,
  rotate: 0,
} as const;

/**
 * Task flow — the canonical trio (completed/pending/skipped of scheduled,
 * always summing to 100%), created-vs-resolved with net backlog flow
 * ("are we drowning or draining?") and the cycle-time trend.
 */
export default function TaskFlowPanel({ points, prevPoints, totals, cycle, compare, phrase, loading }: Props) {
  const categories = points.map((p) => p.label);

  const stackedSeries: ApexAxisChartSeries = [
    { name: 'Completed', type: 'column', data: points.map((p) => p.scheduledCompleted) },
    { name: 'Pending', type: 'column', data: points.map((p) => p.scheduledPending) },
    { name: 'Skipped', type: 'column', data: points.map((p) => p.scheduledSkipped) },
    { name: 'Created', type: 'line', data: points.map((p) => p.created) },
  ];
  const stackedOptions: ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'line', stacked: true },
    colors: ['#339274', '#C9A961', '#5A6275', ADMIN.petrol],
    stroke: { width: [0, 0, 0, 2.5], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '58%', borderRadius: 3 } },
    xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: axisLabels },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      title: { text: 'Tasks', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
      labels: { formatter: (v: number) => formatNumber(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
    },
    grid: { borderColor: 'rgba(11,59,46,0.08)', xaxis: { lines: { show: false } } },
    legend: { show: false },
    tooltip: { theme: 'light', shared: true, intersect: false },
  };

  const trioSeries: ApexAxisChartSeries = [
    { name: 'Completion %', data: points.map((p) => p.completionPct) },
    { name: 'Pending %', data: points.map((p) => p.pendingPct) },
    { name: 'Skip %', data: points.map((p) => p.skipPct) },
  ];
  if (compare && prevPoints.length > 0) {
    trioSeries.push({
      name: 'Completion % (previous)',
      data: points.map((_, i) => prevPoints[i]?.completionPct ?? null),
    });
  }
  const trioOptions: ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'line' },
    colors: ['#339274', '#C9A961', '#5A6275', 'rgba(51,146,116,0.55)'],
    stroke: { curve: 'smooth', width: [2.5, 2, 2, 2], dashArray: [0, 0, 0, 6] },
    xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: axisLabels },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 4,
      title: { text: '% of scheduled', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
      labels: { formatter: (v: number) => `${Math.round(v)}%`, style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
    },
    grid: { borderColor: 'rgba(11,59,46,0.08)', xaxis: { lines: { show: false } } },
    legend: { ...baseChartOptions.legend, position: 'bottom' },
    tooltip: { theme: 'light', shared: true, intersect: false, y: { formatter: (v: number | null) => (v === null ? '—' : `${v}%`) } },
  };

  const netOptions: ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'area' },
    colors: [totals.netFlow > 0 ? '#9F1239' : '#1F5A47'],
    stroke: { curve: 'smooth', width: 2.5 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0 } },
    xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: axisLabels },
    yaxis: {
      title: { text: 'Created − resolved', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
      labels: { formatter: (v: number) => formatNumber(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
    },
    annotations: { yaxis: [{ y: 0, borderColor: 'rgba(11,59,46,0.4)', strokeDashArray: 4 }] },
    grid: { borderColor: 'rgba(11,59,46,0.08)', xaxis: { lines: { show: false } } },
    tooltip: {
      theme: 'light',
      y: { formatter: (v: number) => `${v > 0 ? '+' : ''}${formatNumber(v)} net (positive = backlog growing)` },
    },
  };

  const cycleOptions: ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'line' },
    colors: [ADMIN.petrol],
    stroke: { curve: 'smooth', width: 2.5 },
    markers: { size: 3, strokeColors: '#FAF7EE', strokeWidth: 1.5 },
    xaxis: { categories: cycle.map((c) => c.label), axisBorder: { show: false }, axisTicks: { show: false }, labels: axisLabels },
    yaxis: {
      min: 0,
      title: { text: 'Median hours', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
      labels: { formatter: (v: number) => String(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
    },
    grid: { borderColor: 'rgba(11,59,46,0.08)', xaxis: { lines: { show: false } } },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (v: number | null, opts?: { dataPointIndex: number }) => {
          const n = opts ? (cycle[opts.dataPointIndex]?.n ?? 0) : 0;
          return v === null ? 'No completions' : `${Math.round(v)} h median of ${formatNumber(n)} completed`;
        },
      },
    },
  };

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={5}
      aria-label="Task flow"
      className={`${CARD_CLASS} p-4`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className={ADMIN_EYEBROW_CLASS}>Task flow · created → completed / pending / skipped</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
          <span>
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
              {formatNumber(totals.created)}
            </span>{' '}
            created
          </span>
          <span title="Of tasks scheduled in the window, by current status">
            <span className="font-extrabold tabular-nums" style={{ color: '#1F5A47' }}>
              {totals.completionPct ?? '—'}%
            </span>{' '}
            completed +{' '}
            <span className="font-extrabold tabular-nums" style={{ color: '#8A5A08' }}>
              {totals.pendingPct ?? '—'}%
            </span>{' '}
            pending +{' '}
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.slate }}>
              {totals.skipPct ?? '—'}%
            </span>{' '}
            skipped of {formatNumber(totals.scheduled)} scheduled
          </span>
          <span title="Created minus resolved (completed + skipped) in the window">
            net backlog{' '}
            <span className="font-extrabold tabular-nums" style={{ color: totals.netFlow > 0 ? '#9F1239' : '#1F5A47' }}>
              {totals.netFlow > 0 ? '+' : ''}
              {formatNumber(totals.netFlow)}
            </span>{' '}
            {phrase.replace('vs ', 'window vs ') && ''}
          </span>
        </div>
      </div>

      {loading && points.length === 0 ? (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <div className="h-56 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
          <div className="h-56 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
        </div>
      ) : (
        <>
          <div className="mt-2 grid gap-4 lg:grid-cols-2">
            <div>
              <div className="flex items-center justify-between px-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                  Scheduled per period, by current status · created line
                </p>
                <RateTrioLegend />
              </div>
              <Chart type="line" height={230} options={stackedOptions} series={stackedSeries} />
            </div>
            <div>
              <p className="px-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                Rate trio · always sums to 100% of scheduled
              </p>
              <Chart type="line" height={230} options={trioOptions} series={trioSeries} />
            </div>
          </div>
          <div className="mt-2 grid gap-4 border-t border-[rgba(11,59,46,0.10)] pt-3 lg:grid-cols-2">
            <div>
              <p className="px-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                Net backlog flow · above zero = drowning, below = draining
              </p>
              <Chart type="area" height={180} options={netOptions} series={[{ name: 'Net flow', data: points.map((p) => p.netFlow) }]} />
            </div>
            <div>
              <p className="px-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                Cycle time · median created → completed
              </p>
              <Chart
                type="line"
                height={180}
                options={cycleOptions}
                series={[{ name: 'Median hours', data: cycle.map((c) => c.medianHours) }]}
              />
            </div>
          </div>
        </>
      )}
    </motion.section>
  );
}
